import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

import { useDisclosure } from '@hooks/index';
import { ConditionalRendering } from '../../conditional-rendering';

const DropdownWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const InputField = styled.input`
    width: 100%;
    padding: 10px;
    border: 2px solid ${({ theme }) => theme.gray7};
    border-radius: 2px;
    cursor: pointer;

    &:disabled {
        background-color: ${({ theme }) => theme.gray10};
        cursor: not-allowed;
    }
`;

const ArrowIcon = styled(FontAwesomeIcon)`
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.gray5};
    pointer-events: none;
`;

const Label = styled.label<{ isRequired?: boolean }>`
    display: block;
    margin-bottom: 5px;

    &.required::after {
        content: '*';
        color: red;
        margin-left: 4px;
    }
`;

const Dropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${({ theme }) => theme.defaultWhite};
    border: 1px solid ${({ theme }) => theme.gray7};
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 10;
`;

const DropdownHeader = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px;
    background-color: ${({ theme }) => theme.gray10};
    border-bottom: 1px solid ${({ theme }) => theme.gray7};
    font-size: 14px;
`;

const Button = styled.button`
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const ItemList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const Item = styled.li`
    padding: 0 8px;
    display: flex;
    align-items: center;
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.gray10};
    }
`;

const Checkbox = styled.input`
    margin-right: 10px;
`;

// Multiselect Component
interface Option {
    key: string | number;
    value: string;
}

interface MultiselectProps<T extends Option> {
    id: string;
    options: T[];
    value: string[] | number[];
    onChange: (selectedValues: string[]) => void;
    onBlur?: () => void;
    onFocus?: () => void;
    placeholder?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    testId?: string;
    label: string;
    shouldParse?: boolean;
}

const Multiselect = <T extends Option>({
    id,
    options,
    value,
    onChange,
    onBlur,
    onFocus,
    placeholder = 'multiselect.label.placeholder',
    isRequired = false,
    isDisabled = false,
    label,
    shouldParse = true,
}: MultiselectProps<T>) => {
    const { isOpen, toggle } = useDisclosure();
    const ref = useRef<HTMLDivElement>(null);
    const intl = useIntl();

    const handleCheckboxChange = (optionValue: string | number) => {
        const updatedSelections = value.includes(optionValue)
            ? value.filter(item => item !== optionValue)
            : [...value, optionValue];

        onChange(updatedSelections);
    };

    const handleDropdownToggle = () => {
        if (!isDisabled) {
            toggle(undefined);
        }
    };

    const handleCheckAll = (e: MouseEvent) => {
        e.preventDefault();
        const allValues = options.map(opt => opt.key);
        onChange(allValues);
    };

    const handleUncheckAll = (e: MouseEvent) => {
        e.preventDefault();
        onChange([]);
    };

    const handleOutsideClick = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
            toggle(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    const renderInputValue = (): string => {
        if (value.length > 0) {
            return `${value.length} of ${options.length} selected`;
        }
        return '';
    };

    return (
        <DropdownWrapper ref={ref}>
            <Label htmlFor={id} isRequired={isRequired} data-testid={`${id}_multiselectLabel`}>
                <FormattedMessage id={label} />
            </Label>

            <div style={{ position: 'relative' }}>
                <InputField
                    id={id}
                    data-testid={`${id}_multiselectInput`}
                    disabled={isDisabled}
                    aria-required={isRequired}
                    value={renderInputValue()}
                    onClick={handleDropdownToggle}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    placeholder={intl.formatMessage({ id: placeholder })}
                    autoComplete="off"
                />

                <ArrowIcon icon={faChevronDown} />
            </div>

            <ConditionalRendering isTrue={isOpen && !!options.length}>
                <Dropdown data-testid={`${id}_multiselectOptions`}>
                    <DropdownHeader>
                        <Button onClick={handleCheckAll} data-testid={`${id}_multiselectOptionsCheckAll`}>
                            <FontAwesomeIcon style={{ marginRight: '2px' }} icon={faCheck} size="sm" />
                            <FormattedMessage id="multiselect.label.checkAll" />
                        </Button>
                        <Button onClick={handleUncheckAll} data-testid={`${id}_multiselectOptionsUncheckAll`}>
                            <FontAwesomeIcon style={{ marginRight: '2px' }} icon={faTimes} size="sm" />
                            <FormattedMessage id="multiselect.label.unCheckAll" />
                        </Button>
                        <Button onClick={handleDropdownToggle} data-testid={`${id}_multiselectOptionsClose`}>
                            <FontAwesomeIcon icon={faTimesCircle} size="sm" />
                        </Button>
                    </DropdownHeader>
                    <ItemList>
                        {options.map(option => (
                            <Item
                                key={option.key}
                                onClick={() => handleCheckboxChange(option.key)}
                                data-testid={`${id}_multiselectOption_${option.key}`}
                            >
                                <Checkbox
                                    type="checkbox"
                                    checked={value.includes(option.key)}
                                    data-testid={`${id}_multiselectOptionsCheckbox_${option.key}`}
                                />
                                <ConditionalRendering isTrue={shouldParse}>
                                    <FormattedMessage id={option.value} />
                                </ConditionalRendering>
                                <ConditionalRendering isTrue={!shouldParse}>{option.value}</ConditionalRendering>
                            </Item>
                        ))}
                    </ItemList>
                </Dropdown>
            </ConditionalRendering>
        </DropdownWrapper>
    );
};

export default Multiselect;
